import { Component, OnInit } from '@angular/core';
import { ProjectService } from '../Services/project.services';
import { ProjectLaborService } from '../Services/project-labor.service';
import { ProjectLaborCost } from '../objects/project-labor-cost';
import { Project } from '../objects/Project';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface WorkerRow {
  workerName: string;
  trade: string;
  projects: string[];
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  totalCost: number;
  billedAmount: number;
  otRisk: 'none' | 'medium' | 'high';
}

export interface ProjectResourceSummary {
  project: Project;
  workers: WorkerRow[];
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalLaborCost: number;
  workerCount: number;
}

@Component({
  selector: 'app-resource-planning',
  templateUrl: './resource-planning.component.html',
  styleUrls: ['./resource-planning.component.css']
})
export class ResourcePlanningComponent implements OnInit {
  isLoading = false;
  activeProjects: Project[] = [];
  inactiveProjects: Project[] = [];
  allLaborEntries: ProjectLaborCost[] = [];
  activeProjectSummaries: ProjectResourceSummary[] = [];
  inactiveProjectSummaries: ProjectResourceSummary[] = [];
  workerRoster: WorkerRow[] = [];

  activeView: 'roster' | 'projects' = 'roster';
  workerColumns = ['workerName', 'trade', 'projects', 'regularHours', 'overtimeHours', 'totalHours', 'totalCost', 'otRisk'];

  constructor(
    private projectService: ProjectService,
    private laborService: ProjectLaborService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;

    this.projectService.getProjects().subscribe({
      next: (projects) => {
        const all = projects || [];
        this.activeProjects = all.filter(p => p.isActive !== false);
        this.inactiveProjects = all.filter(p => p.isActive === false);

        if (all.length === 0) {
          this.isLoading = false;
          return;
        }

        // Load labor for ALL projects (active + inactive)
        const laborCalls = all.map(p =>
          this.laborService.getLaborEntriesByProject(p.id!).pipe(
            catchError(() => of([] as ProjectLaborCost[])),
            map(entries => ({ projectId: p.id!, entries }))
          )
        );

        forkJoin(laborCalls).subscribe({
          next: (results) => {
            this.allLaborEntries = results.flatMap(r =>
              r.entries.map(e => ({ ...e, projectId: r.projectId }))
            );
            this.buildViews();
            this.isLoading = false;
          },
          error: () => { this.isLoading = false; }
        });
      },
      error: () => { this.isLoading = false; }
    });
  }

  private buildProjectSummary(project: Project): ProjectResourceSummary {
    const entries = this.allLaborEntries.filter(e => e.projectId === project.id);
    const workerMap = this.groupByWorker(entries, [project.title || '']);
    const workers = Object.values(workerMap);
    return {
      project,
      workers,
      totalRegularHours: workers.reduce((s, w) => s + w.regularHours, 0),
      totalOvertimeHours: workers.reduce((s, w) => s + w.overtimeHours, 0),
      totalLaborCost: workers.reduce((s, w) => s + w.totalCost, 0),
      workerCount: workers.length
    };
  }

  private buildViews(): void {
    this.activeProjectSummaries = this.activeProjects.map(p => this.buildProjectSummary(p));
    this.inactiveProjectSummaries = this.inactiveProjects.map(p => this.buildProjectSummary(p));

    // Worker roster uses only active project labor
    const activeLaborEntries = this.allLaborEntries.filter(e =>
      this.activeProjects.some(p => p.id === e.projectId)
    );
    const workerMap = this.groupByWorker(activeLaborEntries, []);
    Object.values(workerMap).forEach(w => {
      const projectNames = new Set<string>();
      activeLaborEntries
        .filter(e => e.workerName === w.workerName)
        .forEach(e => {
          const p = this.activeProjects.find(pr => pr.id === e.projectId);
          if (p?.title) { projectNames.add(p.title); }
        });
      w.projects = Array.from(projectNames);
    });
    this.workerRoster = Object.values(workerMap).sort((a, b) => b.totalHours - a.totalHours);
  }

  private groupByWorker(entries: ProjectLaborCost[], defaultProjects: string[]): Record<string, WorkerRow> {
    const map: Record<string, WorkerRow> = {};
    entries.forEach(e => {
      if (!map[e.workerName]) {
        map[e.workerName] = {
          workerName: e.workerName,
          trade: e.trade,
          projects: [...defaultProjects],
          regularHours: 0,
          overtimeHours: 0,
          totalHours: 0,
          totalCost: 0,
          billedAmount: 0,
          otRisk: 'none'
        };
      }
      const w = map[e.workerName];
      w.regularHours += e.regularHours || 0;
      w.overtimeHours += e.overtimeHours || 0;
      w.totalHours += (e.regularHours || 0) + (e.overtimeHours || 0) + (e.doubleTimeHours || 0);
      w.totalCost += e.totalCost || 0;
      w.billedAmount += e.billedAmount || 0;
    });
    Object.values(map).forEach(w => {
      if (w.overtimeHours > 16) { w.otRisk = 'high'; }
      else if (w.overtimeHours > 4) { w.otRisk = 'medium'; }
      else { w.otRisk = 'none'; }
    });
    return map;
  }

  get totalWorkersAcrossAllProjects(): number { return this.workerRoster.length; }
  get totalOvertimeWorkers(): number { return this.workerRoster.filter(w => w.overtimeHours > 0).length; }
  get totalAllHours(): number { return this.workerRoster.reduce((s, w) => s + w.totalHours, 0); }
  get totalAllCost(): number { return this.workerRoster.reduce((s, w) => s + w.totalCost, 0); }

  otRiskClass(risk: string): string {
    if (risk === 'high') { return 'ot-high'; }
    if (risk === 'medium') { return 'ot-medium'; }
    return 'ot-none';
  }
}
